import {
  activeOrder,
  allRental,
  daysText,
  fmtNum,
  hasRental,
  lei,
  lineValue,
  orderValue,
  roDate,
  todayIso,
  poMail,
  poMoney,
  poTotals,
  poTsv,
  type PurchaseOrder,
  t,
  intlLocale,
} from '@procumi/shared';
import { useState } from 'react';
import { ApprovalPanel } from '../components/ApprovalPanel';
import { Avatar } from '../components/Avatar';
import { DeliveriesPanel } from '../components/Deliveries';
import { MoreMenu } from '../components/MoreMenu';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';
import { autoSendAllowed } from '../lib/me';
import { legal } from '../lib/market';
import { copyText } from '../lib/clipboard';
import type { Route } from '../routes';
import { rich } from '../lib/rich';

/**
 * Documentul comenzii de achiziție. Pe ecran are deasupra uneltele (tipărire,
 * Excel, mail, anulare); la tipărire rămâne doar foaia, pe A4. PDF-ul se face
 * din fereastra de tipărire a browserului („Salvează ca PDF”): diacriticele ies
 * corect și nu e nevoie de nicio bibliotecă.
 */
export function OrderPage({
  rfqId,
  orderId,
  go,
  toast,
  logoUrl,
}: {
  rfqId: string;
  orderId: string;
  /** logo-ul firmei, din „Contul firmei”; null = fără */
  logoUrl: string | null;
  go: (r: Route) => void;
  toast: Toast;
}) {
  const {
    data: rfq,
    setData,
    error,
    loading,
    reload,
  } = useAsync(() => api.rfq(rfqId), [rfqId], `rfq:${rfqId}`);
  const [canceling, setCanceling] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const back = (
    <button className="back no-print" onClick={() => go({ view: 'rfq', id: rfqId })}>
      {t('‹ Înapoi la cerere')}
    </button>
  );
  if (loading) return <Loading />;
  const order = rfq?.orders?.find((o) => o.id === orderId);
  if (error || !rfq || !order) {
    return (
      <>
        {back}
        <ErrorBox error={error ?? t('Comanda nu a fost găsită.')} onRetry={reload} />
      </>
    );
  }

  const mail = poMail(order);
  const to = order.data.contactEmail;
  const mailto = `mailto:${to}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`;

  const supplement = order.kind === 'supplement';
  const main = supplement ? activeOrder(rfq) : null;
  // „returnată integral” doar la comanda numai de chirie; beton + pompă e „livrată”
  const rental = allRental(order.data);
  const firstIn = order.deliveries.find((d) => d.kind === 'in');
  // chiria reală, din avize: zilele de pe șantier până la retur sau până azi
  const real = orderValue(order, todayIso());
  // ce s-a trecut pe comandă, când diferă de costul real al chiriei
  const estimate =
    Math.abs(real.lei - order.netLei) >= 1 ? t(' (pe comandă, estimat: {0})', lei(order.netLei)) : '';

  const toggleDone = async () => {
    setBusy(true);
    try {
      setData(await api.completeOrder(rfqId, orderId, !order.completedAt));
      toast(
        order.completedAt
          ? t('Comanda {0} e din nou în desfășurare', order.number)
          : t('Comanda {0} e {1} integral', order.number, rental ? t('returnată') : t('livrată')),
      );
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // Echipă, Companie (și proba): comanda pleacă din Procumi
  const auto = autoSendAllowed();

  const sendOrder = async () => {
    const ok = await ask({
      title: order.sentVia === 'procumi' ? t('Trimiți comanda din nou?') : t('Trimiți comanda?'),
      message: t(
        'Comanda {0} pleacă la {1}, cu semnătura ta. Răspunsul furnizorului ajunge la tine.',
        order.number,
        order.data.contactEmail,
      ),
      confirm: t('Trimite'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      setData(await api.sendOrder(rfqId, orderId));
      toast(t('Comanda {0} a plecat la {1}', order.number, order.data.supplierName));
    } catch (e) {
      toast(errMsg(e));
      reload();
    } finally {
      setBusy(false);
    }
  };

  const markSent = async () => {
    setBusy(true);
    try {
      setData(await api.markOrderSent(rfqId, orderId));
      toast(t('Comanda {0} e trecută ca trimisă', order.number));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async () => {
    setBusy(true);
    try {
      await api.cancelOrder(rfqId, orderId, reason.trim());
      toast(t('Comanda {0} a fost anulată', order.number));
      setCanceling(false);
      reload();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const st = orderPill(order, rental, todayIso());
  const d = order.data;

  return (
    <>
      {back}
      <div className="head rfq-head order-head no-print">
        <div>
          <h1>
            {supplement ? t('Suplimentarea') : t('Comanda')} {order.number}
            <span className={`pill ${st.pill}`}>{st.label}</span>
          </h1>
          <p className="order-say">
            {order.status === 'pending'
              ? t('Așteaptă aprobare: nu o trimite încă furnizorului.')
              : order.status === 'rejected'
                ? t('Respinsă la aprobare. Din cerere poți face alta, pornind de la ce ai scris pe asta.')
                : order.status !== 'issued'
                  ? t('Anulată. Rămâne aici pentru istoric; numărul nu se mai folosește.')
                  : order.completedAt
                    ? rental
                      ? t('Returnată integral, marcată pe {0}.', roDate(order.completedAt.slice(0, 10)))
                      : t('Livrată integral, marcată pe {0}.', roDate(order.completedAt.slice(0, 10)))
                    : firstIn
                      ? t('Marfa e pe șantier din {0}.', roDate(firstIn.date))
                      : order.sentAt
                        ? t(
                            'Trimisă furnizorului pe {0}{1}. Așteaptă confirmarea și livrarea.',
                            roDate(order.sentAt.slice(0, 10)),
                            order.sentVia === 'procumi' ? t(', din Procumi, la {0}', to) : '',
                          )
                        : order.mailError
                          ? t('Mailul n-a plecat: {0}', order.mailError)
                          : auto
                            ? t('Încă n-a plecat la furnizor: apasă „Trimite furnizorului”.')
                            : t('Salveaz-o ca PDF și trimite-o furnizorului din programul tău de mail.')}
            {supplement && main ? t(' Suplimentare la comanda {0}, fără concurență.', main.number) : ''}
          </p>
          <div className="meta-chips">
            <span className="mchip">
              <Avatar name={order.supplier.name} />
              {order.supplier.name}
            </span>
            <button className="mchip link-chip" onClick={() => go({ view: 'site', id: rfq.site.id })}>
              <i className="dot" />
              {d.siteName}
            </button>
            <span className="mchip strong">{t('{0} fără TVA', lei(order.netLei))}</span>
            {d.deliveryDate ? (
              <span className="mchip">{t('Livrare: {0}', roDate(d.deliveryDate))}</span>
            ) : null}
            <button className="mchip link-chip" onClick={() => go({ view: 'rfq', id: rfqId })}>
              {t('Cererea: {0}', rfq.title)}
            </button>
            {order.createdBy ? (
              <span className="mchip">
                <Avatar name={order.createdBy.name} />
                {order.createdBy.name}
              </span>
            ) : null}
          </div>
        </div>
        <div className="acts">
          {order.status === 'issued' && to && auto ? (
            <button
              className={order.sentAt ? 'btn' : 'btn primary'}
              disabled={busy}
              onClick={() => void sendOrder()}
            >
              {order.sentVia === 'procumi' ? t('Trimite din nou') : t('Trimite furnizorului')}
            </button>
          ) : null}
          {order.status === 'issued' && to && !auto ? (
            <a className="btn" href={mailto}>
              {t('Mailul către furnizor')}
            </a>
          ) : null}
          {order.status === 'issued' && !auto && !order.sentAt ? (
            <button className="btn" disabled={busy} onClick={() => void markSent()}>
              {t('Am trimis-o')}
            </button>
          ) : null}
          {order.status === 'issued' ? (
            <button
              className="btn"
              disabled={busy}
              title={t('Comanda iese din „în desfășurare” de pe panou')}
              onClick={() => void toggleDone()}
            >
              {order.completedAt
                ? t('Redeschide comanda')
                : rental
                  ? t('Returnată integral')
                  : t('Livrată integral')}
            </button>
          ) : null}
          <button
            className={order.status === 'issued' && auto && to && !order.sentAt ? 'btn' : 'btn primary'}
            onClick={() => window.print()}
          >
            {t('Tipărește / PDF')}
          </button>
          <MoreMenu
            busy={busy}
            items={[
              // la Echipă și Companie, trimiterea de mână rămâne la îndemână, dar nu în față
              ...(order.status === 'issued' && to && auto
                ? [
                    {
                      label: t('Trimite din programul meu de mail'),
                      run: () => window.location.assign(mailto),
                    },
                  ]
                : []),
              ...(order.status === 'issued' && auto && !order.sentAt
                ? [{ label: t('Am trimis-o eu'), run: () => void markSent() }]
                : []),
              { label: t('Copiază pentru Excel'), run: () => void copyText(poTsv(order), toast) },
              ...(order.status === 'issued' || order.status === 'pending'
                ? [
                    {
                      label: order.status === 'pending' ? t('Retrage comanda') : t('Anulează comanda'),
                      run: () => setCanceling(true),
                      danger: true,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>

      {order.status === 'issued' ? <OrderSteps order={order} rental={rental} /> : null}

      <ApprovalPanel
        rfqId={rfqId}
        order={order}
        toast={toast}
        onChange={setData}
        onCompare={() => go({ view: 'rfq', id: rfqId })}
        splitTotal={
          order.kind !== 'supplement' && rfq.chosen?.split?.length ? (rfq.chosen.splitTotal ?? null) : null
        }
      />

      {canceling ? (
        <div className="po-cancel no-print">
          <label className="f">
            {t('De ce o anulezi')}
            <input
              className="inp"
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('s-au schimbat cantitățile')}
              maxLength={500}
            />
            <span className="hint">
              {t(
                'Comanda rămâne în istoric, marcată anulată. Din cerere poți face una nouă, cu număr nou; pornește de la ce ai scris pe asta.',
              )}
            </span>
          </label>
          <div className="acts">
            <button className="btn" onClick={() => setCanceling(false)}>
              {t('Renunță')}
            </button>
            <button className="btn danger" disabled={busy} onClick={() => void cancelOrder()}>
              {t('Anulează comanda {0}', order.number)}
            </button>
          </div>
        </div>
      ) : null}

      {real.rental && order.status === 'issued' ? (
        <div className="read-note no-print rent-real">
          {real.rental.to
            ? t(
                'Chiria a stat {0} pe șantier ({1} → {2}, fără ziua returului).',
                daysText(real.rental.days),
                roDate(real.rental.from),
                roDate(real.rental.to),
              )
            : t('Pe șantier de {0}, din {1}.', daysText(real.rental.days), roDate(real.rental.from))}{' '}
          {real.rental.billedDays > real.rental.days
            ? t('Se plătește chiria minimă, {0} de zile. ', real.rental.billedDays)
            : ''}
          {real.running
            ? rich('Cost până azi: <b>{0}</b> fără TVA{1}.', lei(real.lei), estimate)
            : rich('Cost real: <b>{0}</b> fără TVA{1}.', lei(real.lei), estimate)}
        </div>
      ) : null}

      {order.status === 'pending' || order.status === 'rejected' ? null : (
        <DeliveriesPanel
          rfqId={rfqId}
          order={order}
          rental={hasRental(rfq)}
          toast={toast}
          onUpdated={setData}
        />
      )}

      <OrderDocument order={order} logoUrl={logoUrl} />
    </>
  );
}

/** Starea comenzii într-o etichetă, pentru capul paginii. */
function orderPill(o: PurchaseOrder, rental: boolean, today: string): { label: string; pill: string } {
  if (o.status === 'pending') return { label: t('așteaptă aprobare'), pill: 'wait' };
  if (o.status === 'rejected') return { label: t('respinsă'), pill: 'off' };
  if (o.status !== 'issued') return { label: t('anulată'), pill: 'off' };
  if (o.completedAt) return { label: rental ? t('returnată integral') : t('livrată integral'), pill: 'ok' };
  if (o.deliveries.some((d) => d.kind === 'in')) return { label: t('livrată pe aviz'), pill: 'got' };
  // emisă, dar furnizorul n-o are încă: nu „de livrat”
  if (!o.sentAt) return { label: t('netrimisă'), pill: 'wait' };
  if (o.data.deliveryDate && o.data.deliveryDate < today)
    return { label: t('livrare întârziată'), pill: 'late' };
  return { label: t('de livrat'), pill: 'todo' };
}

/**
 * Drumul comenzii, pe trei pași: emisă, marfa a venit (primul aviz), încheiată.
 * Doar la comenzile emise; aprobarea are panoul ei, mai jos.
 */
function OrderSteps({ order, rental }: { order: PurchaseOrder; rental: boolean }) {
  const first = order.deliveries.find((d) => d.kind === 'in');
  const steps = [
    { label: t('Emisă'), at: roDate(order.issuedAt.slice(0, 10)) },
    { label: t('Pe șantier'), at: first ? roDate(first.date) : null },
    {
      label: rental ? t('Returnată integral') : t('Livrată integral'),
      at: order.completedAt ? roDate(order.completedAt.slice(0, 10)) : null,
    },
  ];
  const now = steps.findIndex((x) => !x.at);
  return (
    <ol className="po-steps no-print" aria-label={t('Drumul comenzii')}>
      {steps.map((x, i) => (
        <li key={x.label} className={x.at ? 'done' : i === now ? 'now' : undefined}>
          <span className="po-step-dot" aria-hidden="true">
            {x.at ? (
              <svg width="10" height="10" viewBox="0 0 10 10">
                <path
                  d="m2 5.2 2 2 4-4.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </span>
          <span className="po-step-t">
            <b>{x.label}</b>
            <small>{x.at ?? (i === now ? t('urmează') : '—')}</small>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Data pe document, cu anul: „6 octombrie 2026”. Un document oficial nu presupune anul. */
const docDate = (t: string) =>
  new Date(t).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Foaia comenzii: ce vede furnizorul, pe ecran și pe hârtie. Datele firmei sunt
 * cele din clipa emiterii; logo-ul e cel de acum (se schimbă rar, iar o copie pe
 * fiecare comandă ar umple baza degeaba).
 */
function OrderDocument({ order, logoUrl }: { order: PurchaseOrder; logoUrl: string | null }) {
  const d = order.data;
  const tot = poTotals(d);
  const money = (n: number) => poMoney(n, d.currency);
  const canceled = order.status === 'canceled' || order.status === 'rejected';
  const pending = order.status === 'pending';

  return (
    <article
      className={`po-doc sheet${canceled ? ' canceled' : ''}${pending ? ' pending' : ''}`}
      aria-label={t('Comanda {0}', order.number)}
    >
      {canceled ? (
        <p className="po-void">
          {t(
            '{0} pe {1}{2}',
            order.status === 'rejected' ? t('Respinsă la aprobare') : t('Anulată'),
            docDate(order.canceledAt!),
            order.cancelReason ? `: ${order.cancelReason}` : '',
          )}
        </p>
      ) : pending ? (
        <p className="po-void">{t('Neemisă: așteaptă aprobare. Nu o trimite furnizorului.')}</p>
      ) : null}

      <header className="po-head">
        <div className="po-party">
          {logoUrl ? <img className="po-logo" src={logoUrl} alt="" /> : null}
          <b className="po-name">{d.buyer.name}</b>
          {d.buyer.cui ? (
            <span>
              {legal().taxId} {d.buyer.cui}
            </span>
          ) : null}
          {d.buyer.regCom ? (
            <span>
              {legal().reg} {d.buyer.regCom}
            </span>
          ) : null}
          {d.buyer.address ? <span>{d.buyer.address}</span> : null}
        </div>
        <div className="po-title">
          <h1>{order.kind === 'supplement' ? t('Comandă suplimentară') : t('Comandă de achiziție')}</h1>
          <span className="po-no">{t('Nr. {0}', order.number)}</span>
          <span>{t('din {0}', docDate(order.issuedAt))}</span>
        </div>
      </header>

      <section className="po-parties">
        <div className="po-party">
          <span className="po-k">{t('Furnizor')}</span>
          <b>{d.supplierName}</b>
          {d.supplierCui ? (
            <span>
              {legal().taxId} {d.supplierCui}
            </span>
          ) : null}
          {d.supplierAddress ? <span>{d.supplierAddress}</span> : null}
          {d.contactName || d.contactEmail ? (
            <span>{t('În atenția: {0}', [d.contactName, d.contactEmail].filter(Boolean).join(' · '))}</span>
          ) : null}
        </div>
        <div className="po-party">
          <span className="po-k">{t('Livrare')}</span>
          <b>{d.siteName}</b>
          <span>{d.deliveryAddress}</span>
          {d.deliveryDate ? (
            <span>{t('Data dorită: {0}', docDate(`${d.deliveryDate}T12:00:00`))}</span>
          ) : null}
          {d.deliveryTerm ? <span>{t('Termen: {0}', d.deliveryTerm)}</span> : null}
        </div>
      </section>

      <p className="po-ref">
        {t(
          'Vă rugăm să livrați produsele de mai jos, conform ofertei dumneavoastră pentru „{0}”.',
          d.rfqTitle,
        )}
      </p>

      <table className="po-table">
        <thead>
          <tr>
            <th className="n">{t('Nr.')}</th>
            <th>{t('Denumire')}</th>
            <th>{t('UM')}</th>
            <th className="n">{t('Cantitate')}</th>
            <th className="n">{t('Preț unitar')}</th>
            <th className="n">{t('Valoare')}</th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l, i) => (
            <tr key={i}>
              <td className="n">{i + 1}</td>
              <td>{l.desc}</td>
              <td>{l.unit}</td>
              <td className="n">{fmtNum(l.qty)}</td>
              <td className="n">{money(l.unitPrice)}</td>
              <td className="n">{money(lineValue(l))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="po-sum">
        <dl>
          <div>
            <dt>{t('Valoare produse')}</dt>
            <dd>{money(tot.lines)}</dd>
          </div>
          {d.transport === 'extra' ? (
            <div>
              <dt>{t('Transport')}</dt>
              <dd>{money(d.transportCost)}</dd>
            </div>
          ) : null}
          {d.extras.map((e) => (
            <div key={e.label}>
              <dt>{e.label}</dt>
              <dd>{money(e.amount)}</dd>
            </div>
          ))}
          <div className="po-net">
            <dt>{t('Total fără TVA')}</dt>
            <dd>{money(tot.net)}</dd>
          </div>
          <div>
            <dt>{t('TVA {0}%', fmtNum(d.vatRate))}</dt>
            <dd>{money(tot.vat)}</dd>
          </div>
          <div className="po-gross">
            <dt>{t('Total de plată')}</dt>
            <dd>{money(tot.gross)}</dd>
          </div>
        </dl>
        {d.currency === 'EUR' && d.fxRate ? (
          <p className="po-fx">{t('Curs de referință: 1 € = {0} lei.', fmtNum(d.fxRate))}</p>
        ) : null}
      </div>

      <section className="po-terms">
        {d.transport === 'included' ? (
          <p>{t('Transportul până la locul de livrare este inclus în prețuri.')}</p>
        ) : null}
        {d.payment ? (
          <p>
            <span className="po-k">{t('Plată')}</span> {d.payment}
          </p>
        ) : null}
        {d.notes ? (
          <p>
            <span className="po-k">{t('Mențiuni')}</span> {d.notes}
          </p>
        ) : null}
        <p>{t('Vă rugăm să confirmați primirea comenzii și data livrării.')}</p>
      </section>

      <footer className="po-sign">
        <div>
          <span className="po-k">{t('Emis de')}</span>
          <span className="po-line">{order.createdBy?.name ?? ''}</span>
          <span className="muted">{d.buyer.name}</span>
        </div>
        <div>
          <span className="po-k">{t('Confirmare furnizor')}</span>
          <span className="po-line" />
          <span className="muted">{t('nume, semnătură, dată')}</span>
        </div>
      </footer>
    </article>
  );
}
